import React, { useState, useEffect } from "react";
import {
  FormControl,
  MenuItem,
  Select,
  Checkbox,
  Typography,
  ListItemText,
  Chip,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { CompanyStatusLabel } from "../OrganizationalStructure/components/company-status-label";

const CompanySelector = ({
  companyOptions = [], // 從父組件接收公司列表
  selectedCom,
  setSelectedCom,
  menuOpen,
  setMenuOpen,
  sx = {},
  iconColor = "#04c500",
  disableOutline = false,
}) => {
  const { t } = useTranslation("common");

  const filteredOptions = companyOptions.filter(
    (c) =>
      c.nodeStatus === "editable" && (!c.orgType || c.orgType === "COMPANY"),
  );
  // 支援不同的資料結構
  const allCompanyIds = filteredOptions.map(
    (c) => c.value || c.id || c.companyId,
  );

  // 渲染公司選項
  const renderMenuItems = () => {
    return companyOptions
      .filter((company) => {
        const isEditable = company.nodeStatus === "editable";
        // 如果沒有 orgType (例如原本的 companyList)，則預設通過
        // 如果有 orgType (例如 orgList)，則必須是 "COMPANY"
        const isCompany = !company.orgType || company.orgType === "COMPANY";

        return isEditable && isCompany;
      })
      .map((company) => {
        // 支援不同的資料結構
        const companyId = company.value || company.id || company.companyId;
        const companyName =
          company.label || company.companyName || company.name;
        const isChecked = selectedCom.includes(companyId);
        // const isFacility = company.type === "Facilities";
        return (
          <MenuItem
            key={companyId}
            value={companyId}
            onClick={(e) => {
              e.stopPropagation();
              const current = [...selectedCom];
              const isSelected = current.includes(companyId);
              const newSelected = isSelected
                ? current.filter((id) => id !== companyId)
                : [...current, companyId];
              setSelectedCom(newSelected);
            }}
            sx={{
              pl: 2,
              my: 1,
              backgroundColor: "white",
            }}
          >
            <Checkbox checked={isChecked} />
            <ListItemText
              primary={<CompanyStatusLabel company={company} id={companyId} name={companyName} />}
              secondary={
                company.hierarchy_name_path || company.hierarchyNamePath
              }
            />
            {/* <Chip
            label={isFacility ? t("Facility") : t("Company")}
            size="small"
            sx={{
              backgroundColor: isFacility ? "#e6f4ea" : "#f0f2f5",
              color: isFacility ? "#1e8e3e" : "dimgrey",
              borderRadius: "4px",
              height: "24px",
              ml: 2,
            }}
          /> */}
          </MenuItem>
        );
      });
  };

  // 渲染選中的值
  const renderValue = (selected) => {
    if (!selected || selected.length === 0) {
      return <div>{t("Please select")}</div>;
    }

    // 支援不同的資料結構
    const isAllSelected =
      allCompanyIds.length > 0 &&
      allCompanyIds.every((id) => selected.includes(id));

    if (isAllSelected) {
      return <div>{t("All Entity")}</div>;
    }

    const selectedLabels = companyOptions
      .filter((item) => {
        const itemId = item.value || item.id || item.companyId;
        return selected.includes(itemId);
      })
      .map((item) => item.label || item.companyName || item.name);

    return selectedLabels.length > 0 ? (
      <div
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {selectedLabels.join(", ")}
      </div>
    ) : (
      <div>{t("Please select")}</div>
    );
  };

  return (
    <FormControl sx={{ width: "100%", ...sx }}>
      <Select
        displayEmpty
        value={selectedCom}
        multiple
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        onOpen={() => setMenuOpen(true)}
        renderValue={renderValue}
        sx={{
          height: 30,
          ...sx,
          "& .MuiSelect-select": {
            padding: "2px 14px",
            minHeight: "0px !important",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          },
          "& .MuiOutlinedInput-root": {
            height: "30px",
            padding: "0px",
          },
          "& .MuiOutlinedInput-input": {
            padding: "5px 14px",
            height: "20px",
          },
          "& .MuiOutlinedInput-notchedOutline": disableOutline
            ? { border: "none" }
            : undefined,
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: disableOutline
              ? "transparent"
              : "rgba(0, 0, 0, 0.23)",
            borderWidth: disableOutline ? 0 : "1px",
          },
          "& .MuiSvgIcon-root": {
            color: iconColor,
            fontSize: "2rem",
          },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              "& .MuiMenuItem-root": {
                minHeight: 32,
                paddingY: 0.5,
                "&.Mui-selected": {
                  backgroundColor: "transparent",
                },
                "&.Mui-selected:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              },
            },
          },
        }}
      >
        {/* 【所有公司】選項 */}
        {(() => {
          const isChecked =
            allCompanyIds.length > 0 &&
            allCompanyIds.every((id) => selectedCom.includes(id));

          return (
            <MenuItem
              key="__ALL__"
              value="__ALL__"
              onClick={(e) => {
                e.stopPropagation();
                const allSelected = allCompanyIds.every((id) =>
                  selectedCom.includes(id),
                );
                const newSelected = allSelected ? [] : [...allCompanyIds];
                setSelectedCom(newSelected);
              }}
              sx={{
                fontWeight: "bold",
                borderBottom: "1px solid #ccc",
              }}
            >
              <Checkbox checked={isChecked} />
              <ListItemText primary={`【${t("All Entity")}】`} />
            </MenuItem>
          );
        })()}

        {/* 渲染公司列表 */}
        {renderMenuItems()}
      </Select>
    </FormControl>
  );
};

export default CompanySelector;
